export const getSignupBillingType = (search) => {
  const value = new URLSearchParams(search || "").get("billingType");
  const normalized = typeof value === "string" ? value.toUpperCase() : "";
  return ["MONTHLY", "YEARLY"].includes(normalized) ? normalized : undefined;
};

// Browser-side mirror of service/signupBilling.service.ts. This file cannot
// import that module — doing so would pull Sentry and the mongoose Account
// model into the LandingPage bundle — so the two must be edited together when
// a plan launches. signupPlanDefaults.spec.ts asserts they agree.
export const DEFAULT_SELF_SERVE_TRIAL_TYPE = "TRIAL_SC_PLAN_AGENCY_V5";

export const EXPLICIT_SELF_SERVE_TRIAL_TYPES = new Set([
  "TRIAL_SC_PLAN_SOLO_V3_1",
  "TRIAL_SC_PLAN_TEAM_V3_1",
  "TRIAL_SC_PLAN_STANDARD_V5",
  "TRIAL_SC_PLAN_TEAM_V5",
  "TRIAL_SC_PLAN_AGENCY_V5",
]);

// FREE is a valid signup link target but is not a trial, so it is accepted
// here and rejected by getExplicitSelfServeTrialType below.
export const DIRECT_SIGNUP_PACKAGE_TYPES = new Set([
  "FREE",
  ...EXPLICIT_SELF_SERVE_TRIAL_TYPES,
]);

export const SELECTED_PLAN_TRIAL_PLANS = new Set([
  "SC_PLAN_SOLO_V3_1",
  "SC_PLAN_TEAM_V3_1",
  "SC_PLAN_STANDARD_V5",
  "SC_PLAN_TEAM_V5",
  "SC_PLAN_AGENCY_V5",
]);

export const getTrialTypeForSelectedPlan = (selectedPlan) =>
  SELECTED_PLAN_TRIAL_PLANS.has(selectedPlan) ? `TRIAL_${selectedPlan}` : null;

export const SIGNUP_OFFER_HEADING = "Create your account to claim your offer";
export const SIGNUP_ACCOUNT_HEADING = "Create your Social Champ account";

// Heading for the signup page, or null to keep the page's trial heading.
// An offer link (the pricing page's "Skip trial" CTA sends offerCode, coupon
// links send couponCode) leads to checkout, so "Start your 14-day free trial"
// promises the wrong thing (z94kyddpvf 4/5). A purchase-required link gets no
// trial at all, and a non-trial package (FREE) is a plain account. The package
// is resolved the same way the submit handler in local.js resolves it.
export const getSignupHeading = (search) => {
  const params = new URLSearchParams(search || "");
  if (params.get("offerCode") || params.get("couponCode")) {
    return SIGNUP_OFFER_HEADING;
  }
  const intent = String(
    params.get("signupIntent") || params.get("st") || "",
  ).toUpperCase();
  if (intent === "PURCHASE") {
    return SIGNUP_ACCOUNT_HEADING;
  }
  let pkg = params.get("package");
  const selectedPlanTrial = getTrialTypeForSelectedPlan(
    params.get("selectedPlan"),
  );
  if (selectedPlanTrial) {
    pkg = selectedPlanTrial;
  } else if (!DIRECT_SIGNUP_PACKAGE_TYPES.has(pkg)) {
    pkg = DEFAULT_SELF_SERVE_TRIAL_TYPE;
  }
  return /TRIAL/i.test(pkg || "") ? null : SIGNUP_ACCOUNT_HEADING;
};

// Reads the package this signup link asked for so the Google button can carry
// it through OAuth. Matched against DIRECT_SIGNUP_PACKAGE_TYPES so `FREE` is
// preserved too — the email path already honours it, and a free-plan signup
// must not become a paid trial just because the user chose Google.
export const getExplicitSelfServeTrialType = (search) => {
  const params = new URLSearchParams(search || "");
  const selectedPlan = params.get("selectedPlan");
  if (SELECTED_PLAN_TRIAL_PLANS.has(selectedPlan)) {
    return `TRIAL_${selectedPlan}`;
  }
  const requestedPackage = params.get("package");
  return DIRECT_SIGNUP_PACKAGE_TYPES.has(requestedPackage)
    ? requestedPackage
    : null;
};
